import React, { useRef, useState, useEffect } from 'react';
import { getScrollTop, findScrollContainer } from '@intelligence/v3/utils/dom';

interface IUseFilterStickyOptions {
  /** 是否启用吸顶计算；关闭时直接返回非吸顶状态 */
  enabled: boolean;
  /** 用于测量触发位置和高度的锚点元素 */
  triggerElementRef: React.RefObject<HTMLDivElement>;
  /** 用于测量实际内容高度的元素；未传时使用锚点元素 */
  heightElementRef?: React.RefObject<HTMLElement>;
  /** 吸顶时距离滚动容器顶部的偏移 */
  topOffset?: number;
  /** 指定滚动容器；未传时向上查找最近滚动容器 */
  scrollContainer?: HTMLElement | null;
}

export interface IUseFilterStickyResult {
  /** 当前筛选器是否进入固定吸顶态 */
  filterFixed: boolean;
  /** 锚点高度，用于吸顶后给原位置占位 */
  filterHeight: number;
}

const getNextFilterFixed = (
  scrollTarget: HTMLElement | Window | undefined | null,
  stickyTriggerOffset: number,
): boolean => {
  if (!scrollTarget || Number.isNaN(stickyTriggerOffset)) {
    return false;
  }

  return getScrollTop(scrollTarget) > stickyTriggerOffset;
};

const getResizeObservedElements = (params: {
  element: HTMLElement;
  measureElement: HTMLElement;
  scrollTarget: HTMLElement | Window | null;
}): HTMLElement[] => {
  const { element, measureElement, scrollTarget } = params;
  const elements = [element, measureElement];

  if (scrollTarget instanceof HTMLElement) {
    elements.push(scrollTarget);
    return Array.from(new Set(elements));
  }

  if (typeof document !== 'undefined' && document.body) {
    elements.push(document.body);
  }

  return Array.from(new Set(elements));
};

/**
 * 计算吸顶触发阈值。
 * window 场景需要扣除 topOffset，容器滚动场景按容器内部坐标计算。
 */
const getStickyTriggerOffset = (params: {
  element?: HTMLElement | null;
  scrollTarget?: HTMLElement | Window | null;
  currentScrollTop: number;
  topOffset: number;
  height: number;
}): number => {
  const { element, scrollTarget, currentScrollTop, topOffset, height } = params;

  if (!element || !scrollTarget) {
    return Number.NaN;
  }

  const elementRect = element.getBoundingClientRect();

  if (scrollTarget === window) {
    return elementRect.top + currentScrollTop + height - topOffset;
  }

  const containerTop = (scrollTarget as HTMLElement).getBoundingClientRect().top;
  const elementTopInContainer = elementRect.top - containerTop + currentScrollTop;

  return elementTopInContainer + height - topOffset;
};

/**
 * Filter 吸顶状态 hook。
 * 负责测量锚点高度、监听滚动和尺寸变化，并输出固定态和占位高度。
 */
export default function useFilterSticky(props: IUseFilterStickyOptions): IUseFilterStickyResult {
  const { enabled, triggerElementRef, heightElementRef, topOffset = 0, scrollContainer } = props;

  const stickyTriggerOffsetRef = useRef(0);
  const filterHeightRef = useRef(0);
  const filterFixedRef = useRef(false);

  const [filterFixed, setFilterFixed] = useState(false);
  const [filterHeight, setFilterHeight] = useState(0);

  filterFixedRef.current = filterFixed;

  const updateFilterFixed = (nextFilterFixed: boolean): void => {
    if (filterFixedRef.current === nextFilterFixed) {
      return;
    }

    filterFixedRef.current = nextFilterFixed;
    setFilterFixed(nextFilterFixed);
  };

  const resetStickyState = (): void => {
    stickyTriggerOffsetRef.current = Number.NaN;
    filterHeightRef.current = 0;
    updateFilterFixed(false);
    setFilterHeight(0);
  };

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      resetStickyState();
      return undefined;
    }

    const element = triggerElementRef.current;

    if (!element) {
      resetStickyState();
      return undefined;
    }

    let scrollTarget: HTMLElement | Window | null = null;
    let rafId = 0;

    const syncFilterFixedState = (): void => {
      updateFilterFixed(getNextFilterFixed(scrollTarget, stickyTriggerOffsetRef.current));
    };

    const updateFilterHeight = (height: number): void => {
      if (Math.abs(filterHeightRef.current - height) <= 1) {
        return;
      }

      filterHeightRef.current = height;
      setFilterHeight(height);
    };

    const removeScrollListener = (): void => {
      scrollTarget?.removeEventListener('scroll', syncFilterFixedState, true);
    };

    const updateScrollTarget = (): void => {
      const nextScrollTarget = scrollContainer || findScrollContainer(element) || window;

      if (nextScrollTarget === scrollTarget) {
        return;
      }

      removeScrollListener();
      scrollTarget = nextScrollTarget;
      scrollTarget.addEventListener('scroll', syncFilterFixedState, { passive: true, capture: true });
    };

    const measure = (): void => {
      const measureElement = heightElementRef?.current || element;

      updateScrollTarget();

      const height = measureElement.offsetHeight || filterHeightRef.current || 0;
      updateFilterHeight(height);

      stickyTriggerOffsetRef.current = getStickyTriggerOffset({
        element,
        scrollTarget,
        currentScrollTop: getScrollTop(scrollTarget),
        topOffset,
        height,
      });
      syncFilterFixedState();
    };

    const scheduleMeasure = (): void => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      rafId = requestAnimationFrame(() => {
        rafId = 0;
        measure();
      });
    };

    const resizeObserver = (() => {
      if (typeof ResizeObserver === 'undefined') {
        return null;
      }

      return new ResizeObserver(scheduleMeasure);
    })();

    measure();
    const measureElement = heightElementRef?.current || element;
    const observedElements = getResizeObservedElements({
      element,
      measureElement,
      scrollTarget,
    });
    observedElements.forEach((observedElement) => {
      resizeObserver?.observe(observedElement);
    });
    window.addEventListener('resize', scheduleMeasure, { passive: true });

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
      removeScrollListener();

      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [enabled, heightElementRef, triggerElementRef, scrollContainer, topOffset]);

  if (!enabled) {
    return {
      filterFixed: false,
      filterHeight: 0,
    };
  }

  return {
    filterFixed,
    filterHeight,
  };
}
